import { create } from "zustand"
import { persist, PersistStorage, StorageValue } from "zustand/middleware"
import {
  PROMOTION_EVENT_POPUP_CONFIG,
  resolvePromotionEventCode,
  type EventActionType,
} from "../EventConfig"

export type { EventActionType }

/** 멀티 액션 이벤트 진행 상태 (동적 키 기반) */
export type EventProgress = Partial<Record<EventActionType, boolean>>

interface PromotionEventState {
  rlEvents: string[]
  expireDates: Record<string, string>
  progresses: Record<string, EventProgress>
  addPromotionEvent: (event: string) => void
  removeEvent: (eventCode: string) => void
  hasEvent: (eventCode: string) => boolean
  getEvents: () => string[]
  getEventsWithAction: (action: EventActionType) => string[]
  markEventAction: (eventCode: string, action: EventActionType) => void
  isEventComplete: (eventCode: string) => boolean
  clearExpiredEvents: () => void
  clear: () => void
}

const STORAGE_KEY = "promotion-event-storage"

/**
 * 만료 날짜 체크
 * @param expireDate - yyyy-mm-dd 형식의 만료 날짜
 * @returns true: 만료됨, false: 유효함
 */
const isExpired = (expireDate?: string): boolean => {
  if (!expireDate) return false

  const expire = new Date(expireDate)
  const now = new Date()

  expire.setHours(0, 0, 0, 0)
  now.setHours(0, 0, 0, 0)

  return now > expire
}

// 이벤트 설정에서 유효한 만료일만 반환 (없거나 만료면 null)
const getValidExpireDate = (rlEvent: string): string | null => {
  const config = PROMOTION_EVENT_POPUP_CONFIG[rlEvent]
  if (!config) return null

  const expireDate = config.defaultExpireDate
  if (!expireDate || isExpired(expireDate)) return null

  return expireDate
}

// 모든 이벤트 중 하나라도 유효한지 체크 (스토리지 만료 체크용)
const hasAnyValidEvent = (
  rlEvents: string[],
  expireDates: Record<string, string>,
): boolean => {
  return rlEvents.some((event) => !isExpired(expireDates[event]))
}

/**
 * 이벤트 상태를 필터링하는 헬퍼 함수
 * removeEvent, clearExpiredEvents에서 공통 사용
 */
const filterEventState = (
  state: Pick<PromotionEventState, "rlEvents" | "expireDates" | "progresses">,
  keepFn: (eventCode: string) => boolean,
) => ({
  rlEvents: state.rlEvents.filter(keepFn),
  expireDates: Object.fromEntries(
    Object.entries(state.expireDates).filter(([k]) => keepFn(k)),
  ),
  progresses: Object.fromEntries(
    Object.entries(state.progresses).filter(([k]) => keepFn(k)),
  ),
})

/**
 * 듀얼 스토리지 (세션 + 로컬 스토리지)
 * 세션 스토리지를 우선으로 사용하고, 로컬 스토리지에도 동시 저장
 */
const createDualStorage = (): PersistStorage<PromotionEventState> => ({
  getItem: (name): StorageValue<PromotionEventState> | null => {
    // 1. 세션 스토리지 먼저 확인
    const sessionValue = sessionStorage.getItem(name)
    if (sessionValue) {
      try {
        const parsed = JSON.parse(sessionValue) as StorageValue<PromotionEventState>

        if (
          hasAnyValidEvent(
            parsed.state?.rlEvents ?? [],
            parsed.state?.expireDates ?? {},
          )
        ) {
          return parsed
        }
        sessionStorage.removeItem(name)
      } catch {
        sessionStorage.removeItem(name)
      }
    }

    // 2. 세션에 없으면 로컬 스토리지 확인
    const localValue = localStorage.getItem(name)
    if (localValue) {
      try {
        const parsed = JSON.parse(localValue) as StorageValue<PromotionEventState>

        if (
          hasAnyValidEvent(
            parsed.state?.rlEvents ?? [],
            parsed.state?.expireDates ?? {},
          )
        ) {
          sessionStorage.setItem(name, JSON.stringify(parsed))
          return parsed
        }
        localStorage.removeItem(name)
      } catch {
        localStorage.removeItem(name)
      }
    }

    return null
  },

  setItem: (name, value) => {
    if (!hasAnyValidEvent(value.state?.rlEvents ?? [], value.state?.expireDates ?? {})) {
      sessionStorage.removeItem(name)
      localStorage.removeItem(name)
      return
    }

    try {
      const valueString = JSON.stringify(value)
      sessionStorage.setItem(name, valueString)
      localStorage.setItem(name, valueString)
    } catch {
      // 스토리지 저장 실패 무시
    }
  },

  removeItem: (name) => {
    sessionStorage.removeItem(name)
    localStorage.removeItem(name)
  },
})

const usePromotionEventStore = create<PromotionEventState>()(
  persist(
    (set, get) => ({
      rlEvents: [],
      expireDates: {},
      progresses: {},

      /** 이벤트 추가 (기존 이벤트에 누적, 중복 방지) */
      addPromotionEvent: (event: string) => {
        // 외부 유입 코드는 환경별로 다를 수 있으므로 스토어에는 canonical 코드만 저장한다.
        const eventCode = resolvePromotionEventCode(event)

        // 먼저 만료된 이벤트 정리 (동일 코드 재발급 시 처리를 위해)
        get().clearExpiredEvents()

        const { rlEvents, expireDates, progresses } = get()
        if (rlEvents.includes(eventCode)) return

        const expireDate = getValidExpireDate(eventCode)
        if (!expireDate) return

        // config.actions 기반 동적 progress 초기화
        const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
        const actions = config.actions
        const newProgress = Object.fromEntries(
          actions.map((a) => [a, false]),
        ) as EventProgress

        set({
          rlEvents: [...rlEvents, eventCode],
          expireDates: { ...expireDates, [eventCode]: expireDate },
          progresses: { ...progresses, [eventCode]: newProgress },
        })
      },

      /** 특정 이벤트 제거 */
      removeEvent: (eventCode: string) => {
        set(filterEventState(get(), (e) => e !== eventCode))
      },

      /** 이벤트 존재 여부 확인 */
      hasEvent: (eventCode: string) => get().rlEvents.includes(eventCode),

      /** 이벤트 목록 반환 */
      getEvents: () => get().rlEvents,

      /** 특정 액션을 포함하는 활성 이벤트 목록 반환 */
      getEventsWithAction: (action: EventActionType) => {
        const { rlEvents } = get()
        return rlEvents.filter((eventCode) => {
          const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
          return config?.actions.includes(action)
        })
      },

      /** 멀티 액션 이벤트의 개별 액션 완료 표시 */
      markEventAction: (eventCode: string, action: EventActionType) => {
        const { rlEvents, progresses } = get()
        if (!rlEvents.includes(eventCode)) return

        const progress = progresses[eventCode]
        if (!progress) return

        set({
          progresses: {
            ...progresses,
            [eventCode]: { ...progress, [action]: true },
          },
        })
      },

      /** 이벤트 완료 여부 확인 (actionsMode에 따라 and/or 조건 적용) */
      isEventComplete: (eventCode: string) => {
        const { rlEvents, progresses } = get()
        if (!rlEvents.includes(eventCode)) return false

        const config = PROMOTION_EVENT_POPUP_CONFIG[eventCode]
        if (!config) return false

        const progress = progresses[eventCode]
        const mode = config.actionsMode ?? "and"

        if (mode === "or") {
          return config.actions.some((action) => progress?.[action] === true)
        }
        return config.actions.every((action) => progress?.[action] === true)
      },

      /** 만료된 이벤트들 제거 */
      clearExpiredEvents: () => {
        const { rlEvents, expireDates } = get()
        const validEvents = rlEvents.filter((e) => !isExpired(expireDates[e]))

        if (validEvents.length === rlEvents.length) return

        set(filterEventState(get(), (e) => validEvents.includes(e)))
      },

      /** 모든 이벤트 제거 */
      clear: () => set({ rlEvents: [], expireDates: {}, progresses: {} }),
    }),
    {
      name: STORAGE_KEY,
      storage: createDualStorage(),
    },
  ),
)

export default usePromotionEventStore
